/**
 * Composants UI tinted — CC-25 design system.
 *
 * Centralise la consommation des variables CSS `--oh-stat-{color}-{bg|text|border|active-bg|active-border}`
 * pour éliminer la duplication des styles inline dispersés dans les pages
 * (ex: `style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}`).
 *
 * Couleurs disponibles : blue, green, red, yellow, cyan, purple, orange, amber.
 * Cf. `frontend/src/index.css` pour la définition dual-mode (dark / light).
 *
 * Composants exposés :
 *  - <StatChip color="blue|green|red|..." active?>label</StatChip>
 *      Pastille colorée (badge utilitaire). `active` pour la version
 *      foncée (état sélectionné, KPI cliqué).
 *  - <PriorityDot color="..." size="sm|md" />
 *      Pastille pleine ronde (sans texte) pour indicateurs visuels.
 *  - <TintedBadge color="..." variant="solid|soft|outline">label</TintedBadge>
 *      Badge plus complet, équivalent du shadcn `<Badge>` mais sur le
 *      design system OH (3 variants).
 *
 * Usage :
 *    import { StatChip, PriorityDot, TintedBadge } from "@/components/ui/oh-tinted";
 *    <StatChip color="green">Synchronisé</StatChip>
 *    <PriorityDot color="red" size="md" />
 *    <TintedBadge color="amber" variant="soft">À résoudre</TintedBadge>
 *
 * Pas d'utilité hors du design system OH — pour les libs tierces, garder
 * leur composant.
 */

import React from "react";

const VALID_COLORS = new Set([
  "blue", "green", "red", "yellow", "cyan",
  "purple", "orange", "amber",
]);

const safeColor = (c) => (VALID_COLORS.has(c) ? c : "blue");


/**
 * StatChip — pastille colorée minimaliste.
 *
 * Props :
 *  - color : "blue" (défaut) | "green" | "red" | "yellow" | "cyan" |
 *            "purple" | "orange" | "amber"
 *  - active : booléen — utilise les variables -active-bg / -active-border
 *  - size   : "xs" (11px) | "sm" (12px, défaut) | "md" (13px)
 *  - className, style, "data-testid", ...rest passés au <span>
 */
export function StatChip({
  color = "blue",
  active = false,
  size = "sm",
  className = "",
  style = {},
  children,
  ...rest
}) {
  const c = safeColor(color);
  const fontSize = size === "xs" ? 11 : size === "md" ? 13 : 12;
  const padding = size === "xs" ? "1px 6px" : "2px 8px";
  const computed = {
    background: active ? `var(--oh-stat-${c}-active-bg)` : `var(--oh-stat-${c}-bg)`,
    color: `var(--oh-stat-${c}-text)`,
    border: `1px solid var(--oh-stat-${c}-${active ? "active-border" : "border"})`,
    borderRadius: 999,
    padding,
    fontSize,
    fontWeight: 600,
    lineHeight: 1.2,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
    ...style,
  };
  return (
    <span className={className} style={computed} {...rest}>
      {children}
    </span>
  );
}


/**
 * PriorityDot — pastille pleine ronde.
 *
 * Props :
 *  - color : voir StatChip
 *  - size  : "sm" (8px) | "md" (12px, défaut) | "lg" (16px)
 *  - title : tooltip natif HTML
 */
export function PriorityDot({
  color = "blue",
  size = "md",
  className = "",
  style = {},
  title,
  ...rest
}) {
  const c = safeColor(color);
  const px = size === "sm" ? 8 : size === "lg" ? 16 : 12;
  const computed = {
    width: px,
    height: px,
    borderRadius: "50%",
    background: `var(--oh-stat-${c}-text)`,
    boxShadow: `0 0 0 2px var(--oh-stat-${c}-bg)`,
    display: "inline-block",
    flexShrink: 0,
    ...style,
  };
  return (
    <span
      className={className}
      style={computed}
      title={title}
      aria-label={title || `${c} indicator`}
      role="img"
      {...rest}
    />
  );
}


/**
 * TintedBadge — badge plus complet (3 variants).
 *
 * variant :
 *  - "soft" (défaut) : fond léger, texte couleur, bord léger
 *  - "solid"         : fond couleur, texte clair (var(--oh-text) ou
 *                      contraste blanc selon couleur — ici on garde
 *                      le texte couleur sur fond actif foncé pour
 *                      simplicité)
 *  - "outline"       : transparent, texte + bord couleur
 *
 * Props :
 *  - color, size (sm|md|lg), className, style, ...rest
 */
export function TintedBadge({
  color = "blue",
  variant = "soft",
  size = "sm",
  className = "",
  style = {},
  children,
  ...rest
}) {
  const c = safeColor(color);
  const padding = size === "lg" ? "4px 12px" : size === "md" ? "3px 10px" : "2px 8px";
  const fontSize = size === "lg" ? 13 : size === "md" ? 12 : 11;

  let computed;
  if (variant === "solid") {
    computed = {
      background: `var(--oh-stat-${c}-active-bg)`,
      color: `var(--oh-stat-${c}-text)`,
      border: `1px solid var(--oh-stat-${c}-active-border)`,
    };
  } else if (variant === "outline") {
    computed = {
      background: "transparent",
      color: `var(--oh-stat-${c}-text)`,
      border: `1px solid var(--oh-stat-${c}-text)`,
    };
  } else {
    // soft
    computed = {
      background: `var(--oh-stat-${c}-bg)`,
      color: `var(--oh-stat-${c}-text)`,
      border: `1px solid var(--oh-stat-${c}-border)`,
    };
  }
  computed = {
    ...computed,
    borderRadius: 6,
    padding,
    fontSize,
    fontWeight: 600,
    lineHeight: 1.3,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
    ...style,
  };
  return (
    <span className={className} style={computed} {...rest}>
      {children}
    </span>
  );
}


export default { StatChip, PriorityDot, TintedBadge };
